/* eslint-disable jsx-a11y/anchor-is-valid */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Form, Modal } from "react-bootstrap";
import Moment from "react-moment";
import { ENDPOINT } from "../../services/api-endpoint";
import { getList, getLists, postJobAdd } from "../../services/services";
import AppLoader from "../../components/AppLoader";
import { usePageLoader } from "../../services/usePageLoader";
import MesEmptyState from "../../components/mes-empty-state/mes-empty-state";
import { toast } from "react-toastify";
import { formatRecipeCardTitle } from "../trurecipe/trurecipe-display";
import "../trurecipe/trurecipe-pages.scss";
import "./mes.jobs.scss";

type RecipeOption = {
  id: number;
  name?: string | null;
  cardTitle?: string | null;
  workflowId: number;
};

type WorkflowOrderLink = {
  orderId: string;
  orderLabel: string;
};

function resolveOrderFromRecipe(
  recipeId: string,
  recipes: RecipeOption[],
  workflowOrders: Map<number, WorkflowOrderLink>,
  orders: { id: number; ordercode?: string | null }[],
): { orderId: string; orderLabel: string } {
  const recipe = recipes.find((r) => String(r.id) === recipeId);
  if (!recipe?.workflowId) {
    return { orderId: "", orderLabel: "" };
  }
  const wf = workflowOrders.get(recipe.workflowId);
  if (!wf?.orderId) {
    return { orderId: "", orderLabel: "No order linked" };
  }
  const manualMatch = orders.find((o) => String(o.id) === String(wf.orderId));
  const orderLabel =
    manualMatch?.ordercode?.trim() ||
    wf.orderLabel.trim() ||
    `Order #${wf.orderId}`;
  return {
    orderId: manualMatch ? String(manualMatch.id) : "",
    orderLabel,
  };
}

type JobRow = {
  id: number;
  jobname?: string | null;
  status?: string | null;
  partname?: string | null;
  ordercode?: string | null;
  partproduced?: string | null;
  totalparts?: string | null;
  scrap?: string | null;
  estimatedstartdate?: string | null;
  estimatedenddate?: string | null;
  qualityPct?: number;
  workflowStepCompleted?: number;
  workflowStepTotal?: number;
};

function parseNum(v: unknown): number {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function statusPillClass(status: string | null | undefined): string {
  const s = String(status ?? "").toLowerCase();
  if (s === "open") return "tv-mes-jobs__pill tv-mes-jobs__pill--open";
  if (s === "in progress" || s === "in_progress")
    return "tv-mes-jobs__pill tv-mes-jobs__pill--progress";
  if (s === "complete" || s === "closed") return "tv-mes-jobs__pill tv-mes-jobs__pill--complete";
  if (s === "on hold" || s === "on_hold") return "tv-mes-jobs__pill tv-mes-jobs__pill--hold";
  return "tv-mes-jobs__pill tv-mes-jobs__pill--muted";
}

const STATUS_OPTIONS = ["All Status", "Open", "In Progress", "Complete", "On Hold"] as const;

const MesJobs = () => {
  const [jobCollection, setJobs] = useState<JobRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(9);
  const [pageIndex, setPageIndex] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("All Status");
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;

  const { isLoading, runWithLoading } = usePageLoader();

  const loadJobs = useCallback(
    async (nextPage: number) => {
      await runWithLoading(async () => {
        const serachtext = searchTextRef.current;
        const statusParam =
          statusFilter && statusFilter !== "All Status" ? statusFilter : undefined;

        const resp = await getList(ENDPOINT.JOB_LIST, {
          pagesize: pageSize,
          pagenumber: nextPage,
          serachtext,
          ...(statusParam ? { status: statusParam } : {}),
        });

        const items = Array.isArray(resp) ? resp : (resp as { data?: JobRow[] })?.data || [];
        const count = Array.isArray(resp)
          ? items.length
          : Number((resp as { count?: unknown })?.count || 0);

        setJobs(items as JobRow[]);
        setTotalCount(count);
        setPageIndex(nextPage);
      });
    },
    [statusFilter, runWithLoading, pageSize],
  );

  useEffect(() => {
    void loadJobs(0);
  }, [statusFilter, loadJobs]);

  const [openModal, setOpenModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const [orders, setOrders] = useState<{ id: number; ordercode?: string | null }[]>([]);
  const [recipes, setRecipes] = useState<RecipeOption[]>([]);
  const [workflowOrders, setWorkflowOrders] = useState<Map<number, WorkflowOrderLink>>(new Map());
  const [form, setForm] = useState<{
    orderId: string;
    orderLabel: string;
    recipeId: string;
    totalparts: string;
    priority: string;
  }>({
    orderId: "",
    orderLabel: "",
    recipeId: "",
    totalparts: "100",
    priority: "Medium",
  });

  const loadRefs = useCallback(async () => {
    try {
      const [ordRes, recRes, wfRes] = await Promise.all([
        getLists(ENDPOINT.ORDER_LIST, {
          pagesize: 300,
          pagenumber: 0,
          serachtext: "",
        }),
        getList(ENDPOINT.RECIPE_LIST, { serachtext: "" }),
        getList(ENDPOINT.WORKFLOWS_LIST, {
          pagesize: 500,
          pagenumber: 0,
          serachtext: "",
        }),
      ]);
      const ordRows = (ordRes?.data as { id?: unknown; ordercode?: string | null }[]) ?? [];
      setOrders(
        ordRows
          .filter((r) => r.id != null)
          .map((r) => ({ id: Number(r.id), ordercode: r.ordercode ?? null })),
      );
      const recArr = Array.isArray(recRes) ? recRes : [];
      setRecipes(
        recArr
          .map(
            (r: {
              id?: unknown;
              name?: string | null;
              cardTitle?: string | null;
              workflowId?: unknown;
              workflowid?: unknown;
            }) => ({
              id: Number(r.id),
              name: r.name ?? null,
              cardTitle: r.cardTitle ?? null,
              workflowId: Number(r.workflowId ?? r.workflowid ?? 0),
            }),
          )
          .filter((r) => Number.isFinite(r.id) && r.id > 0),
      );
      const wfRows = Array.isArray(wfRes) ? wfRes : (wfRes as { data?: unknown[] })?.data ?? [];
      const wfMap = new Map<number, WorkflowOrderLink>();
      for (const row of wfRows) {
        const w = row as {
          id?: unknown;
          orderid?: string | null;
          orderId?: string | null;
          order_code?: string | null;
          orderCode?: string | null;
        };
        const wid = Number(w.id);
        const orderId = String(w.orderid ?? w.orderId ?? "").trim();
        if (!Number.isFinite(wid) || wid <= 0) continue;
        const orderLabel = String(w.orderCode ?? w.order_code ?? "").trim();
        wfMap.set(wid, { orderId, orderLabel });
      }
      setWorkflowOrders(wfMap);
    } catch {
      setOrders([]);
      setRecipes([]);
      setWorkflowOrders(new Map());
    }
  }, []);

  const openCreate = () => {
    setValidated(false);
    setForm({
      orderId: "",
      orderLabel: "",
      recipeId: "",
      totalparts: "100",
      priority: "Medium",
    });
    setOpenModal(true);
    void loadRefs();
  };

  const onRecipeChange = (recipeId: string) => {
    const { orderId, orderLabel } = resolveOrderFromRecipe(
      recipeId,
      recipes,
      workflowOrders,
      orders,
    );
    setForm((p) => ({ ...p, recipeId, orderId, orderLabel }));
  };

  useEffect(() => {
    if (!openModal || !form.recipeId || recipes.length === 0) return;
    const { orderId, orderLabel } = resolveOrderFromRecipe(
      form.recipeId,
      recipes,
      workflowOrders,
      orders,
    );
    setForm((p) => {
      if (p.orderId === orderId && p.orderLabel === orderLabel) return p;
      return { ...p, orderId, orderLabel };
    });
  }, [openModal, form.recipeId, recipes, workflowOrders, orders]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const f = e.currentTarget as HTMLFormElement;
    if (!f.checkValidity()) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    void saveJob();
  };

  async function saveJob() {
    const rid = Number(form.recipeId);
    if (!Number.isFinite(rid) || rid <= 0) return;
    const qty = Math.max(1, Math.floor(parseNum(form.totalparts)));
    const oid = form.orderId.trim() ? Number(form.orderId) : undefined;
    const body: Record<string, unknown> = {
      recipeId: rid,
      totalparts: qty,
      priority: form.priority,
      active: true,
    };
    if (oid != null && Number.isFinite(oid) && oid > 0) {
      body.orderId = oid;
    }
    await runWithLoading(async () => {
      try {
        await postJobAdd(body);
        setOpenModal(false);
        await loadJobs(pageIndex);
        toast.success("Job created successfully.");
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Could not create job.");
      }
    });
  }

  const hasRecords = jobCollection.length > 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));

  const toolbar = useMemo(
    () => (
      <div className="tv-mes-jobs__toolbar tv-mes-v2-toolbar">
        <div className="tv-mes-v2-search tv-mes-jobs__search">
          <i className="fa fa-search tv-mes-v2-search__icon" aria-hidden />
          <input
            type="search"
            className="tv-mes-v2-search__input tv-mes-jobs__search-input"
            placeholder="Search Jobs"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                searchTextRef.current = (e.target as HTMLInputElement).value;
                void loadJobs(0);
              }
            }}
          />
        </div>
        <Form.Select
          className="tv-mes-jobs__status-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Form.Select>
        <button type="button" className="tv-mes-v2-btn-orange tv-mes-jobs__add" onClick={openCreate}>
          <i className="fa fa-plus" aria-hidden />
          Add Job
        </button>
      </div>
    ),
    [searchText, statusFilter, loadJobs],
  );

  return (
    <div className="tv-mes-v2-page tv-mes-jobs-page">
      {isLoading && <AppLoader text="Loading jobs..." />}
      <div className="tv-mes-v2-shell tv-mes-jobs">
        <header className="tv-mes-jobs__head tv-mes-v2-hero tv-mes-v2-hero--split">
          <div>
            <p className="tv-mes-v2-page-desc">Schedule and track production jobs.</p>
          </div>
          {hasRecords ? toolbar : (
            <button type="button" className="tv-mes-v2-btn-orange tv-mes-jobs__add" onClick={openCreate}>
              <i className="fa fa-plus" aria-hidden />
              Add Job
            </button>
          )}
        </header>

        {/* {hasRecords ? (
          <div className="tv-mes-jobs__toolbar-row">{toolbar}</div>
        ) : null} */}

        {!hasRecords && !isLoading ? (
          <div className="tv-mes-jobs__empty-wrap">
            <MesEmptyState
              icon="fa-briefcase"
              title="No Jobs Found"
              subtitle="Add your first job to schedule and track production."
            />
            <button type="button" className="tv-mes-v2-btn-orange tv-mes-jobs__empty-cta" onClick={openCreate}>
              <i className="fa fa-plus" aria-hidden />
              Add Job
            </button>
          </div>
        ) : (
          <div className="tv-mes-jobs__grid">
            {jobCollection.map((job) => {
              const total = parseNum(job.totalparts);
              const produced = parseNum(job.partproduced);
              const scrap = parseNum(job.scrap);
              const qp =
                job.qualityPct ??
                (total > 0 ? Math.round((Math.max(0, produced - scrap) / total) * 100) : 0);
              const wfTotal = job.workflowStepTotal ?? 0;
              const wfDone = job.workflowStepCompleted ?? 0;
              const allPending =
                String(job.status ?? "").toLowerCase() === "open" && produced === 0 && wfTotal > 0;
              const steps = wfTotal > 0 ? Array.from({ length: wfTotal }) : [];
              return (
                <Link key={job.id} to={`/mes-jobs/${job.id}`} className="tv-mes-jobs__card-link">
                  <article className="tv-mes-jobs__card">
                    <div className="tv-mes-jobs__card-top">
                      <h2 className="tv-mes-jobs__card-title">{job.jobname ?? "Job"}</h2>
                      <span className={statusPillClass(job.status)}>{job.status ?? "—"}</span>
                    </div>
                    <p className="tv-mes-jobs__order">
                      Order: {job.ordercode?.trim() ? job.ordercode : "—"}
                    </p>
                    <dl className="tv-mes-jobs__dl">
                      <div>
                        <dt>Part</dt>
                        <dd>{job.partname ?? "—"}</dd>
                      </div>
                      <div>
                        <dt>Produced</dt>
                        <dd>
                          {produced}/{total || "—"}
                        </dd>
                      </div>
                      <div>
                        <dt>Est. Start</dt>
                        <dd>
                          {job.estimatedstartdate ? (
                            <Moment format="D MMM YYYY">{job.estimatedstartdate}</Moment>
                          ) : (
                            "—"
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt>Est. End</dt>
                        <dd>
                          {job.estimatedenddate ? (
                            <Moment format="D MMM YYYY">{job.estimatedenddate}</Moment>
                          ) : (
                            "—"
                          )}
                        </dd>
                      </div>
                    </dl>
                    <div className="tv-mes-jobs__card-meters">
                    <div className="tv-mes-jobs__meter">
                      <div className="tv-mes-jobs__meter-head">
                        <span>Workflow Progress</span>
                        <span className="tv-mes-jobs__meter-count">
                          {wfDone}/{wfTotal || "—"} steps
                        </span>
                      </div>
                      <div className="tv-mes-jobs__wf-track" role="img" aria-label="Workflow steps">
                        {steps.length ? (
                          steps.map((_, i) => {
                            let seg = "tv-mes-jobs__wf-seg tv-mes-jobs__wf-seg--pend";
                            if (allPending) {
                              seg = "tv-mes-jobs__wf-seg tv-mes-jobs__wf-seg--track";
                            } else if (i < wfDone) {
                              seg = "tv-mes-jobs__wf-seg tv-mes-jobs__wf-seg--done";
                            } else if (i === wfDone) {
                              seg = "tv-mes-jobs__wf-seg tv-mes-jobs__wf-seg--prog";
                            }
                            return <div key={i} className={seg} style={{ flex: 1 }} />;
                          })
                        ) : (
                          <div className="tv-mes-jobs__wf-seg tv-mes-jobs__wf-seg--pend" style={{ flex: 1 }} />
                        )}
                      </div>
                    </div>
                    <div className="tv-mes-jobs__meter">
                      <div className="tv-mes-jobs__meter-head">
                        <span>Part Quality</span>
                        <span className="tv-mes-jobs__meter-count">{qp}% good</span>
                      </div>
                      <div className="tv-mes-jobs__q-track">
                        <div className="tv-mes-jobs__q-good" style={{ width: `${Math.min(100, Math.max(0, qp))}%` }} />
                        <div
                          className="tv-mes-jobs__q-bad"
                          style={{ width: `${Math.min(100, Math.max(0, 100 - qp))}%` }}
                        />
                      </div>
                    </div>
                    </div>
                  </article>
                </Link>
              );
            })}
          </div>
        )}

        {pageCount > 1 && hasRecords ? (
          <nav className="tv-mes-jobs__pagination" aria-label="Jobs pagination">
            <ul className="pagination">
              <li className={`page-item ${pageIndex === 0 ? "disabled" : ""}`}>
                <button
                  type="button"
                  className="page-link"
                  disabled={pageIndex === 0}
                  onClick={() => void loadJobs(pageIndex - 1)}
                >
                  «
                </button>
              </li>
              {Array.from({ length: pageCount }).map((_, i) => (
                <li key={i} className={`page-item ${i === pageIndex ? "active" : ""}`}>
                  <button type="button" className="page-link" onClick={() => void loadJobs(i)}>
                    {i + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${pageIndex >= pageCount - 1 ? "disabled" : ""}`}>
                <button
                  type="button"
                  className="page-link"
                  disabled={pageIndex >= pageCount - 1}
                  onClick={() => void loadJobs(pageIndex + 1)}
                >
                  »
                </button>
              </li>
            </ul>
          </nav>
        ) : null}

        <Modal
          centered
          show={openModal}
          onHide={() => setOpenModal(false)}
          className="tv-mes-jobs-modal"
          dialogClassName="tv-mes-jobs-modal__dialog"
        >
          <Modal.Header className="tv-mes-jobs-modal__hdr">
            <div>
              <Modal.Title className="tv-mes-jobs-modal__title">Create New Job</Modal.Title>
              <p className="tv-mes-jobs-modal__sub">
                Select a recipe — the linked workflow order, steps, and parameters will apply automatically.
              </p>
            </div>
            <button
              type="button"
              className="close tv-mes-jobs-modal__x"
              aria-label="Close"
              onClick={() => setOpenModal(false)}
            >
              <span aria-hidden>×</span>
            </button>
          </Modal.Header>
          <Modal.Body className="tv-mes-jobs-modal__body">
            <Form noValidate validated={validated} onSubmit={handleSubmit} id="tv-mes-jobs-create-form">
              <Form.Group className="tv-mes-jobs-modal__group tv-mes-jobs-modal__field-block" controlId="jobRecipe">
                <Form.Label className="tv-mes-jobs-modal__label">Recipe (defines steps + parameters)</Form.Label>
                <Form.Select
                  required
                  className="tv-mes-jobs-modal__control tv-mes-jobs-modal__control--full"
                  value={form.recipeId}
                  onChange={(e) => onRecipeChange(e.target.value)}
                >
                  <option value="" disabled>
                    Select recipe
                  </option>
                  {recipes.map((r) => (
                    <option key={r.id} value={r.id}>
                      {formatRecipeCardTitle(r.cardTitle ?? r.name, `Recipe #${r.id}`)}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">Choose a recipe.</Form.Control.Feedback>
              </Form.Group>
              {form.recipeId ? (
                <p className="tv-mes-jobs-modal__hint">
                  <i className="fa fa-check-circle" aria-hidden /> Recipe will auto-attach workflow steps and process
                  parameters
                </p>
              ) : null}
              <div className="tv-mes-jobs-modal__field-block tv-mes-jobs-modal__group">
                <Form.Label className="tv-mes-jobs-modal__label" htmlFor="tv-mes-jobs-order">
                  Order
                </Form.Label>
                <Form.Control
                  id="tv-mes-jobs-order"
                  className="tv-mes-jobs-modal__control tv-mes-jobs-modal__control--full"
                  readOnly
                  disabled
                  value={
                    form.recipeId
                      ? form.orderLabel || "No order linked"
                      : "Select a recipe first"
                  }
                />
              </div>
              <div className="tv-mes-jobs-modal__row2">
                <div className="tv-mes-jobs-modal__cell">
                  <Form.Group controlId="jobQty">
                    <Form.Label className="tv-mes-jobs-modal__label">Quantity</Form.Label>
                    <Form.Control
                      required
                      className="tv-mes-jobs-modal__control tv-mes-jobs-modal__control--full"
                      type="number"
                      min={1}
                      value={form.totalparts}
                      onChange={(e) => setForm((p) => ({ ...p, totalparts: e.target.value }))}
                    />
                  </Form.Group>
                </div>
                <div className="tv-mes-jobs-modal__cell">
                  <Form.Group controlId="jobPri">
                    <Form.Label className="tv-mes-jobs-modal__label">Priority</Form.Label>
                    <Form.Select
                      className="tv-mes-jobs-modal__control tv-mes-jobs-modal__control--full"
                      value={form.priority}
                      onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}
                    >
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>
            </Form>
          </Modal.Body>
          <Modal.Footer className="tv-mes-jobs-modal__ft">
            <Button variant="light" className="tv-mes-jobs-modal__cancel" onClick={() => setOpenModal(false)}>
              Cancel
            </Button>
            <Button type="submit" form="tv-mes-jobs-create-form" className="tv-mes-v2-btn-orange tv-mes-jobs-modal__ok">
              Create Job
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default MesJobs;
